import { useCallback, useEffect, useMemo, useState } from "react";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import { getAllStudents } from "../../../helpers/getAllStudents";
import axios from "../../../axios";
import * as S from "./Funnel.styled";

// Воронка: заявки (сайт + внесені вручну з директу) → пробні → оплати → продовження, відміни, рекомендації.
// Дані: GET /admin/funnel. Звітний період школи — з 9-го по 8-ме.

const SOURCE_LABEL = { instagram: "Instagram", tiktok: "TikTok", telegram: "Telegram", website: "Сайт", referral: "Рекомендація", google: "Google", other: "Інше" };
const STAGE_LABEL = { new: "Нова", contacted: "Написали", trial_booked: "Пробний призначено", trial_done: "Пробний пройшов", paid: "Оплатив", lost: "Відмова" };
const TRIAL_LABEL = { scheduled: ["пробний призначено", "violet"], attended: ["був на пробному", "green"], joined: ["став учнем", "green"], missed: ["не прийшов", "red"], cancelled: ["пробний скасовано", "red"] };
const BY_LABEL = { teacher: "Викладач", student: "Учень", school: "Школа" };

const fmt = (n) => Math.round(n || 0).toLocaleString("uk-UA");
const day = (d) => (d ? new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" }) : "—");
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function periodStart(offset) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() - (now.getDate() < 9 ? 1 : 0), 9);
  return new Date(base.getFullYear(), base.getMonth() + offset, 9);
}
const pctText = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const withId = (name, id) => (id ? `${name} (ID ${id})` : name || "—");

function Funnel() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [students, setStudents] = useState([]);

  const from = periodStart(offset);
  const to = periodStart(offset + 1);

  const load = useCallback(() => {
    setError("");
    axios
      .get(`admin/funnel?from=${iso(from)}&to=${iso(to)}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Не вдалося завантажити воронку"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getAllStudents().then((list) => setStudents(list.filter((s) => !s.isPaused))).catch(() => {}); }, []);

  return (
    <PagesWrapper>
      <TopPageTitle value={"Воронка"} />
      <S.Page>
        <S.PeriodBar>
          <S.RoundBtn onClick={() => setOffset(offset - 1)} aria-label="Попередній період">←</S.RoundBtn>
          <span>Період {day(from)} – {day(new Date(to.getTime() - 86400000))}</span>
          <S.RoundBtn onClick={() => setOffset(offset + 1)} disabled={offset >= 0} aria-label="Наступний період">→</S.RoundBtn>
        </S.PeriodBar>
        {error && <S.ErrorText>{error}</S.ErrorText>}
        {data && (
          <>
            <Conversion data={data} />
            <S.Row2>
              <Sources bySource={data.bySource} total={data.conversion.leads} />
              <Money p={data.paymentStats} />
            </S.Row2>
            <Leads leads={data.leads} onChange={load} />
            <S.Row2>
              <Retention r={data.retention} />
              <Referrals items={data.referrals} />
            </S.Row2>
            <Cancellations c={data.cancelStats} items={data.cancellations} students={students} onChange={load} />
          </>
        )}
      </S.Page>
    </PagesWrapper>
  );
}

function Conversion({ data }) {
  const c = data.conversion;
  const t = data.trialStats;
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Воронка продажів</S.H>
          <S.Hint>Заявки — з сайту і внесені вручну. Пробні — з розділу «Пробні уроки». «Стали учнями» — перша оплата, пробний «Зайшов на навчання» або заявка «Оплатив» (одна людина — один раз). Гроші — з «Оплата учнів» (облік з 13.10.2026).</S.Hint>
        </div>
      </S.CardHead>
      <S.Steps>
        <S.Step><S.Big>{c.leads}</S.Big><S.Small>заявок</S.Small></S.Step>
        <S.Step><S.Big>{c.trials}</S.Big><S.Small>пробних призначено</S.Small><S.Rate>{pctText(c.leadToTrialPct)} від заявок</S.Rate></S.Step>
        <S.Step><S.Big>{c.trialsAttended}</S.Big><S.Small>прийшли на пробний</S.Small><S.Rate>{pctText(c.trialToAttendedPct)} від призначених</S.Rate></S.Step>
        <S.Step><S.Big>{c.newStudents}</S.Big><S.Small>стали учнями</S.Small><S.Rate>{pctText(c.attendedToPaidPct)} від тих, хто прийшов</S.Rate></S.Step>
      </S.Steps>
      {c.unpaidConverted?.length > 0 && (
        <S.Hint style={{ marginTop: 10, color: "#b45309" }}>
          Стали учнями, але оплату не внесено: {c.unpaidConverted.map((r) => r.name).join(", ")}. Внеси пакет в «Оплата учнів» — тоді сума піде у виручку.
        </S.Hint>
      )}
      {(t.missed > 0 || t.cancelled > 0) && (
        <S.Hint style={{ marginTop: 10 }}>Не прийшли на пробний: {t.missed} · пробних скасовано: {t.cancelled}</S.Hint>
      )}
    </S.Card>
  );
}

function Sources({ bySource, total }) {
  const rows = Object.entries(bySource).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  return (
    <S.Card>
      <S.CardHead><S.H>Звідки заявки</S.H></S.CardHead>
      {rows.length === 0 ? <S.Empty>Заявок за період ще немає</S.Empty> : (
        <S.Bars>
          {rows.map(([k, n]) => (
            <S.BarRow key={k}>
              <span>{SOURCE_LABEL[k] || k}</span>
              <S.BarTrack><i style={{ width: `${(n / Math.max(total, 1)) * 100}%` }} /></S.BarTrack>
              <b>{n}</b>
            </S.BarRow>
          ))}
        </S.Bars>
      )}
    </S.Card>
  );
}

function Money({ p }) {
  return (
    <S.Card>
      <S.CardHead><S.H>Оплати</S.H></S.CardHead>
      <S.Stats>
        <S.Stat><S.Big>{fmt(p.revenue)} грн</S.Big><S.Small>оплат за період</S.Small></S.Stat>
        <S.Stat><S.Big>{p.newStudents}</S.Big><S.Small>нових учнів</S.Small></S.Stat>
        <S.Stat><S.Big>{p.renewals}</S.Big><S.Small>продовжень</S.Small></S.Stat>
        <S.Stat $tone="amber"><S.Big>{pctText(p.firstPack16PlusPct)}</S.Big><S.Small>нових взяли 16+ уроків (ціль ≥30%)</S.Small></S.Stat>
      </S.Stats>
    </S.Card>
  );
}

function Leads({ leads, onChange }) {
  const empty = { name: "", phone: "", source: "", referredBy: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("active");

  const shown = useMemo(() => leads.filter((l) => (filter === "all" ? true : filter === "active" ? !["paid", "lost"].includes(l.stage || "new") : (l.stage || "new") === filter)), [leads, filter]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Вкажи імʼя");
    if (!form.source) return setErr("Обери, звідки прийшла заявка");
    setBusy(true); setErr("");
    axios.post("admin/leads", form)
      .then(() => { setForm(empty); setOpen(false); onChange(); })
      .catch(() => setErr("Не вдалося зберегти"))
      .finally(() => setBusy(false));
  };
  const setStage = (id, stage) => {
    const body = { stage };
    if (stage === "lost") {
      const reason = window.prompt("Причина відмови (дорого, немає часу, не відповідає, обрали інших…)") || "";
      body.lostReason = reason;
    }
    axios.patch(`admin/leads/${id}`, body).then(onChange).catch(() => alert("Не вдалося оновити"));
  };

  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Заявки</S.H>
          <S.Hint>З сайту приходять самі. Написали в директ Instagram/TikTok чи Telegram — додай кнопкою справа, щоб усе рахувалося разом.</S.Hint>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <S.Select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Фільтр">
            <option value="active">В роботі</option>
            <option value="all">Усі</option>
            {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </S.Select>
          <S.Btn onClick={() => setOpen(!open)}>{open ? "Закрити" : "+ Заявка"}</S.Btn>
        </div>
      </S.CardHead>

      {open && (
        <S.Form onSubmit={submit}>
          <label>Імʼя*<S.Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Телефон / нік<S.Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+380… або @nick" /></label>
          <label>Звідки*
            <S.Select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="" disabled>— обери —</option>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </S.Select>
          </label>
          {form.source === "referral" && (
            <label>Хто порадив<S.Input value={form.referredBy} onChange={(e) => setForm({ ...form, referredBy: e.target.value })} /></label>
          )}
          <label>Нотатка<S.Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="ціль, вік, зручний час" /></label>
          <S.Btn type="submit" disabled={busy}>{busy ? "Зберігаю…" : "Додати"}</S.Btn>
          {err && <S.ErrorText>{err}</S.ErrorText>}
        </S.Form>
      )}

      {shown.length === 0 ? <S.Empty>Немає заявок у цьому фільтрі</S.Empty> : (
        <S.Table>
          <table>
            <thead><tr><th>Дата</th><th>Імʼя</th><th>Контакт</th><th>Звідки</th><th>Пробний</th><th>Етап</th></tr></thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l._id}>
                  <td>{day(l.createdAt)}</td>
                  <td><b>{l.name}</b>{l.notes ? <S.Hint>{l.notes}</S.Hint> : null}{l.lostReason ? <S.Hint>Причина: {l.lostReason}</S.Hint> : null}</td>
                  <td>{l.phone || "—"}</td>
                  <td>{SOURCE_LABEL[l.source] || l.source}{l.referredBy ? <S.Hint>від: {l.referredBy}</S.Hint> : null}</td>
                  <td>{l.trialStatus ? <S.Tag $c={TRIAL_LABEL[l.trialStatus]?.[1]}>{TRIAL_LABEL[l.trialStatus]?.[0] || l.trialStatus}</S.Tag> : "—"}</td>
                  <td>
                    <S.Select value={l.stage || "new"} onChange={(e) => setStage(l._id, e.target.value)} aria-label="Етап">
                      {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </S.Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </S.Table>
      )}
    </S.Card>
  );
}

function Retention({ r }) {
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Продовження</S.H>
          <S.Hint>Напиши цим учням про продовження, поки уроки не закінчились. Активних: {r.active}, на паузі: {r.paused}.</S.Hint>
        </div>
      </S.CardHead>
      <S.Small style={{ fontWeight: 700, margin: "4px 0" }}>Залишилось 1–2 уроки ({r.lowBalance.length})</S.Small>
      {r.lowBalance.length === 0 ? <S.Empty>Немає</S.Empty> : (
        <S.List>{r.lowBalance.map((s) => <li key={s._id}><span><b>{withId(s.name, s.studentId)}</b> · {withId(s.teacherName, s.teacherCode)}</span><S.Tag $c="amber">{s.totalLessons} ур.</S.Tag></li>)}</S.List>
      )}
      <S.Small style={{ fontWeight: 700, margin: "12px 0 4px" }}>0 уроків або борг ({r.zeroOrDebt.length})</S.Small>
      {r.zeroOrDebt.length === 0 ? <S.Empty>Немає</S.Empty> : (
        <S.List>{r.zeroOrDebt.map((s) => <li key={s._id}><span><b>{withId(s.name, s.studentId)}</b> · {withId(s.teacherName, s.teacherCode)}</span><S.Tag $c="red">{s.totalLessons} ур.</S.Tag></li>)}</S.List>
      )}
    </S.Card>
  );
}

function Referrals({ items }) {
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>«Приведи друга»</S.H>
          <S.Hint>Коли друг оплатив — нарахуй +2 уроки обом («Оплата учнів» → +1 двічі).</S.Hint>
        </div>
      </S.CardHead>
      {items.length === 0 ? <S.Empty>За період рекомендацій ще немає</S.Empty> : (
        <S.List>
          {items.map((r) => (
            <li key={r._id}>
              <span><b>{r.name}</b> ← {r.referredBy || "хто — не вказано"}</span>
              {r.stage === "paid" ? <S.Tag $c="green">оплатив → +2 обом</S.Tag> : <S.Tag>{STAGE_LABEL[r.stage || "new"]}</S.Tag>}
            </li>
          ))}
        </S.List>
      )}
    </S.Card>
  );
}

function Cancellations({ c, items, students, onChange }) {
  const empty = { studentId: "", by: "teacher", reason: "", lessonDate: iso(new Date()), replaced: false };
  const [form, setForm] = useState(empty);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return students.filter((s) => s.name.toLowerCase().includes(q) || String(s.studentId).includes(q)).slice(0, 6);
  }, [query, students]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.studentId) return setErr("Обери учня зі списку");
    setBusy(true); setErr("");
    axios.post("admin/cancellations", form)
      .then(() => { setForm(empty); setQuery(""); setOpen(false); onChange(); })
      .catch(() => setErr("Не вдалося зберегти"))
      .finally(() => setBusy(false));
  };
  const remove = (id) => {
    if (!window.confirm("Видалити цей запис?")) return;
    axios.delete(`admin/cancellations/${id}`).then(onChange);
  };

  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Відміни уроків</S.H>
          <S.Hint>З платформи записуються самі. Скасували в особистих — внеси тут. Якщо скасовує викладач — заміну шукає школа.</S.Hint>
        </div>
        <S.Btn onClick={() => setOpen(!open)}>{open ? "Закрити" : "+ Відміна"}</S.Btn>
      </S.CardHead>

      {open && (
        <S.Form onSubmit={submit}>
          <label style={{ position: "relative" }}>Учень*
            <S.Input value={query} onChange={(e) => { setQuery(e.target.value); setForm({ ...form, studentId: "" }); }} placeholder="імʼя або ID" />
            {matches.length > 0 && !form.studentId && (
              <S.List style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, background: "#fff", border: "1px solid #E7E5F5", borderRadius: 10, padding: "0 8px" }}>
                {matches.map((s) => (
                  <li key={s._id} style={{ cursor: "pointer" }} onClick={() => { setForm({ ...form, studentId: s._id }); setQuery(`${s.name} (ID ${s.studentId})`); }}>
                    <span>{s.name} (ID {s.studentId})</span><span>{s.teacherName}</span>
                  </li>
                ))}
              </S.List>
            )}
          </label>
          <label>Хто скасував
            <S.Select value={form.by} onChange={(e) => setForm({ ...form, by: e.target.value })}>
              <option value="teacher">Викладач</option>
              <option value="student">Учень</option>
              <option value="school">Школа</option>
            </S.Select>
          </label>
          <label>Дата уроку<S.Input type="date" value={form.lessonDate} onChange={(e) => setForm({ ...form, lessonDate: e.target.value })} /></label>
          <label>Причина<S.Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="захворів, робота…" /></label>
          {form.by === "teacher" && (
            <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={form.replaced} onChange={(e) => setForm({ ...form, replaced: e.target.checked })} /> Знайшли заміну
            </label>
          )}
          <S.Btn type="submit" disabled={busy}>{busy ? "Зберігаю…" : "Додати"}</S.Btn>
          {err && <S.ErrorText>{err}</S.ErrorText>}
        </S.Form>
      )}

      <S.Stats style={{ marginBottom: 14 }}>
        <S.Stat><S.Big>{c.total}</S.Big><S.Small>відмін за період</S.Small></S.Stat>
        <S.Stat $tone="pink"><S.Big>{c.byTeacher}</S.Big><S.Small>скасували викладачі</S.Small></S.Stat>
        <S.Stat><S.Big>{c.byStudent}</S.Big><S.Small>скасували учні</S.Small></S.Stat>
        <S.Stat $tone="amber"><S.Big>{c.teacherReplaced}/{c.byTeacher}</S.Big><S.Small>відмін викладача із заміною</S.Small></S.Stat>
      </S.Stats>

      {c.perTeacher.some((t) => t.teacher > 0) && (
        <>
          <S.Small style={{ fontWeight: 700, marginBottom: 4 }}>Відміни з боку викладачів</S.Small>
          <S.List style={{ marginBottom: 14 }}>
            {c.perTeacher.filter((t) => t.teacher > 0).map((t) => (
              <li key={t.teacherName}><span>{t.teacherName}</span><S.Tag $c={t.teacher > 2 ? "red" : "amber"}>{t.teacher}</S.Tag></li>
            ))}
          </S.List>
        </>
      )}

      {items.length === 0 ? <S.Empty>Відмін за період немає</S.Empty> : (
        <S.Table>
          <table>
            <thead><tr><th>Внесено</th><th>Учень</th><th>Викладач</th><th>Хто скасував</th><th>Причина</th><th>Джерело</th><th></th></tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={x._id}>
                  <td>{day(x.createdAt)}</td>
                  <td>{withId(x.studentName, x.studentCode)}</td>
                  <td>{withId(x.teacherName, x.teacherCode)}</td>
                  <td><S.Tag $c={x.by === "teacher" ? "pink" : undefined}>{BY_LABEL[x.by]}</S.Tag>{x.by === "teacher" && x.replaced ? <S.Hint>заміну знайдено</S.Hint> : null}</td>
                  <td>{x.reason || "—"}</td>
                  <td>{x.source === "platform" ? "платформа" : "вручну"}</td>
                  <td>{x.source === "manual" && <S.Btn $ghost onClick={() => remove(x._id)} aria-label="Видалити">✕</S.Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </S.Table>
      )}
    </S.Card>
  );
}

export default Funnel;
